// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getProductState, getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { configHasOnlyOptionalEmbroideries } from "@store/Framework/Common/Utilities/embroideriesUtilities";
import cloneDeep from "lodash/cloneDeep";

export function canAddToCart(
    state: ApplicationState,
    product: ProductModel,
    productInfo?: ProductInfo,
    configurationCompleted?: boolean,
    variantSelectionCompleted?: boolean,
    configurationSelection?: SafeDictionary<string>,
) {
    /* Store Custom Code - Start */
    const configHasOnlyOptEmb = configHasOnlyOptionalEmbroideries(product.detail?.configuration);
    /* Store Custom Code - End */

    if (
        !product ||
        (!configurationCompleted &&
            product.configurationType !== ConfigurationType.None && // Store Custom Code
            product.detail?.configuration &&
            !product.detail?.configuration.isKit &&
            product.detail?.configuration.configSections &&
            product.detail?.configuration.configSections.length > 0 &&
            !configHasOnlyOptEmb) // Store Custom Code
    ) {
        return false;
    }

    if (product.quoteRequired) {
        var currentCart = getCurrentCartState(state).value;
        if (currentCart && currentCart.status === "AwaitingApprovalEditDraft") {
            return false;
        }
    }

    // Fixed configuration, if any section has CantBuy is true, we return false.
    if (
        product.configurationType === ConfigurationType.Fixed && // Store Custom Code
        product.detail?.configuration &&
        product.detail?.configuration.isKit &&
        product.detail?.configuration.configSections &&
        product.detail?.configuration.configSections.length > 0 &&
        !configHasOnlyOptEmb // Store Custom Code
    ) {
        for (const configSection of product.detail!.configuration.configSections) {
            if (configSection.sectionOptions?.some(x => x.cantBuy)) {
                return false;
            }
        }
    }

    // Standard configuration, check CantBuy property of selected options.
    if (
        configurationCompleted &&
        product.configurationType === ConfigurationType.Standard && // Store Custom Code
        product.detail?.configuration &&
        !product.detail?.configuration.isKit &&
        product.detail?.configuration.configSections &&
        product.detail?.configuration.configSections.length > 0 &&
        configurationSelection &&
        Object.values(configurationSelection).length > 0 &&
        !configHasOnlyOptEmb // Store Custom Code
    ) {
        const sectionOptions = Object.values(configurationSelection).filter(sectionOptionId => !!sectionOptionId);
        if (sectionOptions) {
            for (const configSection of product.detail!.configuration.configSections) {
                const selectedOptions = configSection.sectionOptions?.filter(x => sectionOptions.indexOf(x.id) > -1);
                if (selectedOptions && selectedOptions.every(x => x.cantBuy)) {
                    return false;
                }
            }
            return true;
        }
    }

    const { canAddToCart, allowBackOrder } = getSettingsCollection(state).productSettings;
    const availability =
        productInfo?.inventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === (productInfo?.unitOfMeasure?.toLowerCase() || ""),
        )?.availability || undefined;
    return (
        canAddToCart &&
        !product.cantBuy &&
        (product.canAddToCart ||
            /* Store Custom Code - Start */
            (!product.isVariantParent &&
                productSelectionCompleted(
                    state.pages.productDetails.variantSelection,
                    configurationSelection || state.pages.productDetails.configurationSelection,
                    configHasOnlyOptEmb,
                    variantSelectionCompleted,
                    configurationCompleted,
                ) /* Store Custom Code - End */ &&
                (allowBackOrder || (availability && availability.messageType !== 2)) &&
                !product.canConfigure))
    );
}

// it currently doesn't appear that we need to memoize this. We may need to later after we resolve some issues with DataState not caching results as well as it could.
export function getComputedVariantProduct(
    parentProductState: ReturnType<typeof getProductStateByPath>,
    variantProductState: ReturnType<typeof getProductState>,
) {
    if (!variantProductState.value) {
        return parentProductState;
    }

    if (!parentProductState.value) {
        return parentProductState;
    }

    if (parentProductState.value.id === variantProductState.value.id) {
        return parentProductState;
    }

    const { value: variantProduct } = variantProductState;

    const computedProduct = cloneDeep(parentProductState.value);
    Object.assign(computedProduct, {
        productNumber: variantProduct.productNumber,
        customerProductNumber: variantProduct.customerProductNumber,
        smallImagePath: variantProduct.smallImagePath,
        mediumImagePath: variantProduct.mediumImagePath,
        largeImagePath: variantProduct.largeImagePath,
        imageAltText: variantProduct.imageAltText,
        id: variantProduct.id,
        quoteRequired: variantProduct.quoteRequired,
        productTitle: variantProduct.productTitle,
        images: variantProduct.images,
        trackInventory: variantProduct.trackInventory,
        minimumOrderQty: variantProduct.minimumOrderQty,
        canonicalUrl: variantProduct.canonicalUrl,
        unitOfMeasures: variantProduct.unitOfMeasures,
        isVariantParent: false,
        canAddToWishlist: variantProduct.canAddToWishlist,
        cantBuy: variantProduct.cantBuy,
        allowZeroPricing: variantProduct.allowZeroPricing,
        manufacturerItem: variantProduct.manufacturerItem,
        detail: variantProduct.detail,
        content:
            variantProduct.content && variantProduct.content.htmlContent !== ""
                ? variantProduct.content
                : parentProductState.value.content,
        documents:
            variantProduct.documents && variantProduct.documents.length > 0
                ? variantProduct.documents.concat(parentProductState.value.documents || [])
                : parentProductState.value.documents,
        specifications:
            variantProduct.specifications && variantProduct.specifications.length > 0
                ? variantProduct.specifications
                : parentProductState.value.specifications,
        attributeTypes:
            variantProduct.attributeTypes && variantProduct.attributeTypes.length > 0
                ? variantProduct.attributeTypes
                : parentProductState.value.attributeTypes,
        badges:
            variantProduct.badges && variantProduct.badges.length > 0
                ? variantProduct.badges
                : parentProductState.value.badges,
        properties: variantProduct.properties,
        configurationType: variantProduct.configurationType, // Store Custom Code
    });

    return {
        isLoading: parentProductState.isLoading,
        id: variantProductState.id,
        value: computedProduct,
    };
}

/* Store Custom Code - Start */
function productSelectionCompleted(
    variantSelection: SafeDictionary<string>,
    configurationSelection: SafeDictionary<string>,
    configurationContainsOnlyEmbroideries?: boolean | null,
    variantSelectionCompleted?: boolean,
    configurationCompleted?: boolean,
) {
    const hasVariant = Object.values(variantSelection).length > 0;
    const hasConfiguration = Object.values(configurationSelection).length > 0 || configurationContainsOnlyEmbroideries;
    const isConfigurationOrEmbroideryCompleted = configurationCompleted || configurationContainsOnlyEmbroideries;

    if (hasVariant && hasConfiguration) {
        return variantSelectionCompleted && isConfigurationOrEmbroideryCompleted;
    }

    if (hasVariant) {
        return variantSelectionCompleted;
    }

    if (hasConfiguration) {
        return isConfigurationOrEmbroideryCompleted;
    }

    return false;
}
/* Store Custom Code - End */
